import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GlobalCTA from "@/components/GlobalCTA";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Shield, Users, Code, Heart, Mic } from "lucide-react";
import { Link } from "react-router-dom";
import yellowBlazer from "@/assets/yellow-blazer.png";
import PageSEO from "@/components/PageSEO";
import { breadcrumb, personSchema, profilePageSchema } from "@/lib/schemas";

const About = () => {
  const credentials = [
    {
      icon: Shield,
      role: "Broker & Mortgage Strategist",
      title: "FOR MY CLIENTS",
      subtitle: "The Protector",
      description: "Most agents see a commission check; I see a deed. Since I know what it feels like to not have a key to my own door, I guard your equity like a watchdog. I audit contracts, spot loopholes, and translate bank jargon so you win."
    },
    {
      icon: Users,
      role: "Team Lead & CEO",
      title: "FOR MY AGENTS",
      subtitle: "The Blueprint",
      description: "I do not train salespeople; I build CEOs. I built Fresh Vision Realty to be the school I never had. I give you the operational blueprint—contracts, compliance, and tech—so you stop guessing and start scaling."
    },
    {
      icon: Code,
      role: "The Code Owner",
      title: "AS YOUR TECH ADVISOR",
      subtitle: "The Innovator",
      description: "I got tired of seeing brilliant agents held hostage by 'Tech Bros' and monthly subscriptions. So I learned to code. I teach you to build your own platforms, own your data, and fire your digital landlords."
    },
    {
      icon: Heart,
      role: "The Living Proof",
      title: "AS YOUR MENTOR",
      subtitle: "The Matriarch",
      description: "I retired my husband and homeschool our children not due to luck, but since I engineered a life that allows it. I help women stop wearing 'Exhaustion' as a badge of honor. We build for legacy, not just for bills."
    }
  ];

  const ecosystem = [
    {
      label: "THE SUMMIT",
      title: "Tech Founder Summit",
      description: "Stop renting your business. Learn to build your own platforms.",
      buttonText: "Register for Free",
      link: "/tech-founder-summit",
      external: false
    },
    {
      label: "THE REALTY",
      title: "Fresh Vision Realty",
      description: "Work with a team that protects your equity like it's their own.",
      buttonText: "Work With My Team",
      link: "https://freshvisionrealty.com",
      external: true
    },
    {
      label: "THE INSTITUTE",
      title: "Mabel & Augustus",
      description: "Closing the wealth gap through education and ownership.",
      buttonText: "Support The Mission",
      link: "https://webuildfamily.com",
      external: true
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="About Marquetta Bryant | Architect of Freedom — FVR Founder"
        description="From homelessness to multi-state brokerage founder. Marquetta Bryant's story, the MARQUETTA Method, and the Mabel & Augustus Institute."
        canonical="https://marquettabryant.com/about"
        jsonLd={[personSchema, profilePageSchema, breadcrumb([{name:"Home",path:"/"},{name:"About",path:"/about"}])]}
      />
      <Header />
      <main id="main-content">
      
      {/* Hero Section with Photo */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-gradient-to-br from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-40 h-40 md:w-52 md:h-52 rounded-full mx-auto mb-8 overflow-hidden shadow-2xl ring-4 ring-primary/20">
              <img src={yellowBlazer} alt="Marquetta Bryant" className="w-full h-full object-cover object-top" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-serif">
              From Survival to Structure.
            </h1>
            <p className="text-xl text-muted-foreground">
              The story of building systems that hold.
            </p>
          </div>
        </div>
      </section>
      
      {/* The Letter Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg max-w-none space-y-6 font-serif text-foreground/90 leading-relaxed">
              <p className="text-xl italic text-muted-foreground">Dear Friend,</p>
              
              <p>I'm really glad you're here.</p>
              
              <p>People often ask how I know so much about stability. The answer is simple. I spent the first half of my life without it.</p>
              
              <p>I was raised by a single mother in Southeast Washington, D.C., in a place where you learned early how to stay alert. Where systems were not built to protect you. Where ownership felt distant and softness felt rare. I watched people work hard and still lose ground. I learned how quickly life can shift when there is no margin.</p>
              
              <p>As an adult, I experienced homelessness myself. Not as a concept. As reality. I know what it feels like to not have a key to your own door. I know the quiet fear that follows you even when you are doing everything you can to stay afloat.</p>
              
              <p>I didn't just make it through those seasons. I paid attention.</p>
              
              <p>I learned that the difference between poverty and leverage is not effort. It is ownership and information. It is structure. It is knowing how the system works and refusing to be locked out of it again.</p>
              
              <p>That understanding shaped everything I built next.</p>
              
              <p>I am a licensed real estate broker and mortgage strategist, but more than that, I am an architect. I design paths that lead to stability. When I sit across from a buyer, I am not thinking about a closing date. I am thinking about protection. I audit contracts carefully. I guard equity seriously. I translate the language banks use so families can make decisions with clarity instead of fear.</p>
              
              <p>When I lead agents, I do not teach hustle. I teach structure. I built Fresh Vision Realty to be the education I never had. I give my agents systems, standards, and support so they can build real businesses without burning their lives down to do it.</p>
              
              <p>When I work with technology, I do it with intention. I learned how to build and own my platforms so no one else controls my data, my income, or my reach. Digital space deserves the same ownership mindset as physical property.</p>
              
              <p>This work did not stop with me.</p>
              
              <p>I founded the Mabel and Augustus Institute to carry the legacy forward. It is named after the people who should have had access to stability but did not. The institute exists to restore what was denied by creating access to education, economic structure, and family-centered ownership. It is where everything I build gets anchored back into community and continuity.</p>
              
              <p className="font-semibold text-foreground">Legacy is not what you leave behind. It is what you build while you are still here.</p>
              
              <p>And when I mentor women, I lead as living proof.</p>
              
              <p>I retired my husband. I homeschool our children. Not through luck. Through design. I believe businesses exist to fund families, not replace them. Exhaustion is not a badge of honor. Peace is not something you earn after you break yourself.</p>
              
              <p>I built my life so it could move with me. So my family could stay together. So work could support living instead of competing with it.</p>
              
              <p>This space exists for anyone who is tired of surviving. For anyone who wants ownership that feels steady. For anyone who wants systems that protect time, money, and family.</p>
              
              <p>You don't need to prove yourself here. You need structure that holds you once you arrive.</p>
              
              <p className="font-semibold text-foreground">Welcome. You're safe here.</p>
              
              <p className="text-muted-foreground">With care,</p>
              
              <p className="text-3xl font-script mt-8 text-primary" style={{ fontFamily: "'Brush Script MT', cursive" }}>
                Marquetta
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Credentials Section */}
      <section className="py-20 md:py-32 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              WHY I AM THE ARCHITECT
            </h2>
            <p className="text-xl text-muted-foreground italic">
              I do not just hold a license; I hold a life sentence in resilience.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {credentials.map((cred, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <cred.icon className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                        {cred.title}
                      </span>
                      <h3 className="text-xl font-bold mt-1 mb-1">{cred.subtitle}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{cred.role}</p>
                      <p className="text-foreground/80 leading-relaxed">{cred.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* The Ecosystem Section */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            HOW WE BUILD TOGETHER
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {ecosystem.map((item, index) => (
              <Card key={index} className="border-2 hover:border-primary/50 transition-colors group">
                <CardContent className="p-8 text-center">
                  <span className="text-xs font-bold uppercase tracking-widest text-accent">
                    {item.label}
                  </span>
                  <h3 className="text-2xl font-bold mt-4 mb-4">{item.title}</h3>
                  <p className="text-muted-foreground mb-8">{item.description}</p>
                  {item.external ? (
                    <a href={item.link} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        {item.buttonText}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </a>
                  ) : (
                    <Link to={item.link}>
                      <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        {item.buttonText}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Media Section */}
      <section className="py-12 bg-muted/30 border-t">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-xl font-bold mb-3 uppercase tracking-wide">
              Media & Speaking
            </h3>
            <p className="text-muted-foreground mb-6">
              Booking Marquetta for a podcast or keynote? Access the official press materials.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/speaking">
                <Button variant="premium" className="gap-2">
                  <Mic className="w-4 h-4" />
                  Book Marquetta
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <GlobalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default About;
